import { uniq, intersection } from 'lodash';
import { parseWildcardDates } from '../helpers/dateHelpers';
import { extractValueFromOrderTags } from './OrderTags';
import { DiscountScope } from '../plugins/commonTypes/ProductTypes';
import getLuxonImpl from '../plugins/commonLibraries/timezone/timeboxImpl';
import { isFreeMealPriceRule, isPriceRuleValid, isRuleActive, isRuleWithTimePatternActive, shouldApplyCustomPriceRule, shouldRuleBeAppliedToShop, } from '../priceRules/priceRulesHelpers';
const isVoucherValid = (discount, options) => {
    const voucherPattern = (discount === null || discount === void 0 ? void 0 : discount.voucherPattern) || [];
    if (voucherPattern.length === 0) {
        return false;
    }
    const suppliedVouchers = (options === null || options === void 0 ? void 0 : options.vouchers) || [];
    return intersection(voucherPattern, suppliedVouchers).length > 0;
};
export const shouldApplyDiscount = (discount, options) => {
    if (!discount) {
        return false;
    }
    const voucherPattern = (discount === null || discount === void 0 ? void 0 : discount.voucherPattern) || [];
    return voucherPattern.length === 0 || isVoucherValid(discount, options);
};
export const getEffectiveQuantityDiscountLimit = (discount, session) => {
    var _a, _b, _c, _d;
    if (!discount) {
        return undefined;
    }
    if (((_b = (_a = discount === null || discount === void 0 ? void 0 : discount.options) === null || _a === void 0 ? void 0 : _a.qualifyingProductIds) === null || _b === void 0 ? void 0 : _b.length) && session.hasCartProductsMap) {
        const totalQualifyingProductsQty = discount.options.qualifyingProductIds.reduce((acc, curr) => {
            var _a;
            return acc + (((_a = session.cartProductsMap[curr]) === null || _a === void 0 ? void 0 : _a.quantity) || 0);
        }, 0);
        if (discount.options.quantityLimit !== undefined) {
            return Math.min(discount.options.quantityLimit, totalQualifyingProductsQty);
        }
        return totalQualifyingProductsQty;
    }
    return (((_c = discount === null || discount === void 0 ? void 0 : discount.options) === null || _c === void 0 ? void 0 : _c.quantityLimit) ||
        ((_d = discount === null || discount === void 0 ? void 0 : discount.options) === null || _d === void 0 ? void 0 : _d.quantityPerUserLimit));
};
const matchesShopContext = (rule, selectedShopId) => {
    if (selectedShopId === null)
        return true;
    return shouldRuleBeAppliedToShop(rule.shopPattern, selectedShopId);
};
const matchesRuleOptions = (rule, options, productCatalog, session) => {
    var _a, _b, _c, _d, _e;
    if (!rule.options) {
        return true;
    }
    const channel = options === null || options === void 0 ? void 0 : options.channel;
    const customerType = options === null || options === void 0 ? void 0 : options.customerType;
    const deliveryMethod = options === null || options === void 0 ? void 0 : options.deliveryMethod;
    const isPriceRuleAvailableForChannel = [undefined, null].includes(channel) ||
        !((_a = rule.options.channels) === null || _a === void 0 ? void 0 : _a.length) ||
        rule.options.channels.includes(channel);
    const isPriceRuleAvailableForCustomerType = [undefined, null].includes(customerType) ||
        !((_b = rule.options.customerTypes) === null || _b === void 0 ? void 0 : _b.length) ||
        rule.options.customerTypes.includes(customerType);
    const isPriceRuleAvailableForDeliveryMethod = !Number.isInteger(deliveryMethod) ||
        !((_c = rule.options.deliveryMethods) === null || _c === void 0 ? void 0 : _c.length) ||
        rule.options.deliveryMethods.includes(deliveryMethod);
    const cartProductsMap = session.cartProductsMap;
    const isPriceRuleAvailableByQualifyingProducts = !((_e = (_d = rule === null || rule === void 0 ? void 0 : rule.options) === null || _d === void 0 ? void 0 : _d.qualifyingProductIds) === null || _e === void 0 ? void 0 : _e.length) ||
        (session.hasCartProductsMap && rule.options.qualifyingProductIds.find((qp) => cartProductsMap === null || cartProductsMap === void 0 ? void 0 : cartProductsMap[qp]));
    const isPriceRuleAvailableInGivenTimeSlot = isRuleActive(rule, productCatalog.pickupTimeZone);
    return (isPriceRuleAvailableForChannel &&
        isPriceRuleAvailableForCustomerType &&
        isPriceRuleAvailableForDeliveryMethod &&
        isPriceRuleAvailableByQualifyingProducts &&
        isPriceRuleAvailableInGivenTimeSlot);
};
const matchesRuleDateContext = (rule, productCatalog) => {
    const { currentDate, pickupTimeZone, userTimeZone } = productCatalog;
    const { Formats: TimeBoxFormats, timebox: TimeBox } = getLuxonImpl();
    if (Array.isArray(rule.orderingDatePattern)) {
        const shopCurrentDate = TimeBox
            .convertRelativeStringDate(currentDate, userTimeZone, pickupTimeZone)
            .format(TimeBoxFormats.YYYY_MM_DD)
            .exec();
        const limitDates = uniq(rule.orderingDatePattern.reduce((carry, dateFilterItem) => {
            return [...carry, ...parseWildcardDates(dateFilterItem, new Date(currentDate))];
        }, []));
        return limitDates.indexOf(shopCurrentDate) !== -1;
    }
    if (Array.isArray(rule.weekDay)) {
        return isRuleActive(rule, pickupTimeZone);
    }
    return false;
};
const matchesMembershipContext = (rule, orderTags, options) => {
    if (shouldApplyCustomPriceRule(rule, options)) {
        return true;
    }
    const membership = extractValueFromOrderTags('Membership', orderTags);
    const subscriptionCreatedDate = new Date(extractValueFromOrderTags('SubscriptionCreatedDate', orderTags));
    const switchedDateTag = extractValueFromOrderTags('SubscriptionSwitchedDate', orderTags);
    const subscriptionSwitchedDate = ['null', 'undefined', ''].includes(switchedDateTag)
        ? undefined
        : new Date(switchedDateTag);
    return isPriceRuleValid(membership, rule, subscriptionCreatedDate, subscriptionSwitchedDate);
};
export const evaluatePriceRules = ({ priceRules, productCatalog, options, orderTags, session, }) => {
    const filterBasedOnVoucher = (rule) => shouldApplyDiscount(rule, options);
    const { selectedShopId } = productCatalog;
    const filteredByContext = priceRules
        .filter((rule) => matchesShopContext(rule, selectedShopId))
        .filter(filterBasedOnVoucher)
        .filter((rule) => {
        if (isFreeMealPriceRule(rule)) {
            return extractValueFromOrderTags('FirstMeal', orderTags) === 'true';
        }
        return true;
    })
        .filter((rule) => matchesRuleOptions(rule, options, productCatalog, session));
    const filteredRules = filteredByContext.filter((rule) => matchesRuleDateContext(rule, productCatalog));
    const priceRuleAcc = filteredRules.reduce((acc, curr) => {
        if (curr.productSpecific === false) {
            const c = curr;
            return Object.assign(Object.assign({}, acc), { global: [...acc.global, c] });
        }
        const c = curr;
        return Object.assign(Object.assign({}, acc), { specific: [...acc.specific, c] });
    }, { specific: [], global: [] });
    const computedGlobalPriceRules = priceRuleAcc.global
        .filter((rule) => matchesMembershipContext(rule, orderTags, options))
        .filter(filterBasedOnVoucher)
        .sort((r1, r2) => r2.options.priority - r1.options.priority);
    return {
        computedGlobalPriceRules,
        priceRuleAcc,
    };
};
const exceedsQualifiedDiscountLimit = (discount, session, quantity) => {
    var _a, _b;
    if (!discount) {
        return false;
    }
    if (!session.hasCartProductsMap) {
        return false;
    }
    const cartProductsMap = session.cartProductsMap;
    if (!((_b = (_a = discount.options) === null || _a === void 0 ? void 0 : _a.qualifyingProductIds) === null || _b === void 0 ? void 0 : _b.length)) {
        return false;
    }
    const hasQualifyingProduct = discount.options.qualifyingProductIds.find((qualifyingProductId) => cartProductsMap === null || cartProductsMap === void 0 ? void 0 : cartProductsMap[qualifyingProductId]);
    if (!hasQualifyingProduct) {
        return true;
    }
    if (!discount.discountId) {
        return false;
    }
    const discountId = discount.discountId;
    const usageKey = discountId.toString();
    const currentUsage = session.qualifiedDiscountUsage[usageKey] || 0;
    const limit = getEffectiveQuantityDiscountLimit(discount, session);
    if (limit !== undefined && limit > currentUsage) {
        session.qualifiedDiscountUsage[usageKey] = currentUsage + (quantity || 1);
        return false;
    }
    return true;
};
const exceedsQuantityPerUserLimit = (discount, session, quantity) => {
    var _a, _b;
    if (!discount)
        return false;
    if (!session.hasCartProductsMap)
        return false;
    const discountId = discount.discountId;
    if (!discountId)
        return false;
    const limit = (_a = discount.options) === null || _a === void 0 ? void 0 : _a.quantityPerUserLimit;
    if (!limit)
        return false;
    const cartProductsMap = session.cartProductsMap;
    if (!((_b = discount.voucherPattern) === null || _b === void 0 ? void 0 : _b.length)) {
        const hasProduct = discount.productPattern.some((productId) => cartProductsMap === null || cartProductsMap === void 0 ? void 0 : cartProductsMap[productId]);
        if (!hasProduct) {
            return false;
        }
    }
    const usageKey = discountId.toString();
    const currentUsage = session.qualifiedDiscountUsage[usageKey] || 0;
    if (limit > currentUsage) {
        session.qualifiedDiscountUsage[usageKey] = currentUsage + (quantity || 1);
        return false;
    }
    return true;
};
const matchesDiscountOptions = (discount, options, productCatalog, quantity, session) => {
    var _a, _b, _c, _d, _e, _f;
    if (!discount) {
        return false;
    }
    const exceededPriceRuleIds = session.exceededPriceRuleIds.size > 0
        ? session.exceededPriceRuleIds
        : new Set((options.exceededPriceRules || []).map((pr) => { var _a; return (_a = pr.id) === null || _a === void 0 ? void 0 : _a.toString(); }).filter(Boolean));
    if ((discount === null || discount === void 0 ? void 0 : discount.discountId) && exceededPriceRuleIds.has(discount.discountId.toString())) {
        return false;
    }
    const discountTimePattern = discount === null || discount === void 0 ? void 0 : discount.discountTimePattern;
    if (discountTimePattern === null || discountTimePattern === void 0 ? void 0 : discountTimePattern.length) {
        const isActive = isRuleWithTimePatternActive(discountTimePattern, productCatalog.pickupTimeZone);
        if (!isActive) {
            return false;
        }
    }
    const channel = options === null || options === void 0 ? void 0 : options.channel;
    if (channel && ((_b = (_a = discount === null || discount === void 0 ? void 0 : discount.options) === null || _a === void 0 ? void 0 : _a.channels) === null || _b === void 0 ? void 0 : _b.length) && !discount.options.channels.includes(channel)) {
        return false;
    }
    const deliveryMethod = options === null || options === void 0 ? void 0 : options.deliveryMethod;
    if (Number.isInteger(deliveryMethod) &&
        ((_d = (_c = discount === null || discount === void 0 ? void 0 : discount.options) === null || _c === void 0 ? void 0 : _c.deliveryMethods) === null || _d === void 0 ? void 0 : _d.length) &&
        !discount.options.deliveryMethods.includes(Number(deliveryMethod))) {
        return false;
    }
    const customerType = options === null || options === void 0 ? void 0 : options.customerType;
    if (customerType &&
        ((_f = (_e = discount === null || discount === void 0 ? void 0 : discount.options) === null || _e === void 0 ? void 0 : _e.customerTypes) === null || _f === void 0 ? void 0 : _f.length) &&
        !discount.options.customerTypes.includes(customerType)) {
        return false;
    }
    if (exceedsQualifiedDiscountLimit(discount, session, quantity)) {
        return false;
    }
    if (exceedsQuantityPerUserLimit(discount, session, quantity)) {
        return false;
    }
    return true;
};
export const evaluateProductDiscount = ({ product, productSelectionNode, quantity, options, productCatalog, discountScopes, groupContext, session, }) => {
    var _a, _b, _c;
    const discount = product.discount;
    if (!discount) {
        return null;
    }
    const scopedDiscount = (() => {
        var _a, _b;
        if ((_a = discount === null || discount === void 0 ? void 0 : discount.options) === null || _a === void 0 ? void 0 : _a.context) {
            return discount.options.context.includes(groupContext) ? discount : null;
        }
        const discountScope = ((_b = discount === null || discount === void 0 ? void 0 : discount.options) === null || _b === void 0 ? void 0 : _b.discountScope) || DiscountScope.All;
        return discountScopes.includes(discountScope) ? discount : null;
    })();
    if (!scopedDiscount) {
        return null;
    }
    const effectiveQuantity = (_a = quantity !== null && quantity !== void 0 ? quantity : productSelectionNode.quantity) !== null && _a !== void 0 ? _a : 1;
    const hasCartProducts = session.hasCartProductsMap;
    if (((_c = (_b = scopedDiscount === null || scopedDiscount === void 0 ? void 0 : scopedDiscount.options) === null || _b === void 0 ? void 0 : _b.qualifyingProductIds) === null || _c === void 0 ? void 0 : _c.length) && !hasCartProducts) {
        return null;
    }
    const isEligible = matchesDiscountOptions(scopedDiscount, options, productCatalog, effectiveQuantity, session);
    if (!isEligible) {
        return null;
    }
    return scopedDiscount;
};
